import { useEffect, useMemo, useRef, useState } from "react";
import { GripVertical, Loader2, Save, RotateCcw, ArrowUpToLine, ArrowDownToLine, Edit2, Search, Flag, Calendar, Wand2, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { toast } from "sonner";
import { MODES, MODE_META } from "../../lib/modes";
import { StarRating } from "./StarRating";
import { CampaignResetDialog } from "./CampaignResetDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LISTS = [{ id: "warmup", label: "Warm-ups" }, { id: "challenge", label: "Bonus challenges" }];
const SORT_LABEL = { difficulty: "easiest → hardest", title: "A → Z" };

function ConfirmSortDialog({ by, onCancel, onConfirm }) {
  return (
    <AlertDialog open={!!by} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <AlertDialogContent data-testid="order-sort-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            All manual updates will be overridden. Every question in this list will be re-sorted {SORT_LABEL[by] || by}
            {by === "difficulty" ? " by stars (ties keep their current order)" : ""}, and the new order is saved immediately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="order-sort-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid="order-sort-confirm-btn">Yes, sort {SORT_LABEL[by] || by}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// "Auto-rate difficulty": programmatic 1-5 stars from each solution's structure, then (optionally) sort.
function AutoRateDialog({ open, unrated, total, onCancel, onConfirm }) {
  const [overwrite, setOverwrite] = useState(false);
  const [thenSort, setThenSort] = useState(true);
  useEffect(() => { if (open) { setOverwrite(false); setThenSort(true); } }, [open]);
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <AlertDialogContent data-testid="order-autorate-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> Auto-rate difficulty</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>Each solution is analysed for lines, loops, ifs, functions, string/list operations and more, and given 1–5 stars with a "why" you can see by hovering the stars. Junior Blocks grids are rated from the auto-solver's route. Nothing is asked of the AI.</p>
              <p data-testid="order-autorate-summary"><span className="font-medium text-foreground">{unrated}</span> of {total} listed questions are unrated{unrated !== total ? "; rated ones are kept unless you tick overwrite" : ""}.</p>
              <label className="flex items-start gap-2 cursor-pointer text-foreground">
                <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} className="mt-0.5" data-testid="order-autorate-overwrite" />
                <span>Overwrite all existing stars <span className="text-muted-foreground">(including ones set by hand)</span></span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-foreground">
                <Checkbox checked={thenSort} onCheckedChange={(v) => setThenSort(!!v)} className="mt-0.5" data-testid="order-autorate-sort" />
                <span>Then auto-sort easiest → hardest <span className="text-muted-foreground">(overrides manual order)</span></span>
              </label>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="order-autorate-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onConfirm({ overwrite, thenSort })} data-testid="order-autorate-confirm">{overwrite ? "Yes, re-rate everything" : "Rate unrated questions"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function OrderRow({ q, index, total, onMove, onEdit, onRate, draggable, onDragStart, onDragOver, onDrop, isDragging, isOver }) {
  const [pos, setPos] = useState(index + 1);
  useEffect(() => setPos(index + 1), [index]);
  const commit = () => {
    const n = Math.min(total, Math.max(1, parseInt(pos, 10) || index + 1));
    if (n !== index + 1) onMove(q.question_id, n - 1); else setPos(index + 1);
  };
  return (
    <li
      draggable={draggable}
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart(q.question_id); }}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop(index); }}
      className={`flex items-center gap-2 sm:gap-3 rounded-lg border px-2 sm:px-3 py-2 bg-card transition-colors ${isDragging ? "opacity-40" : ""} ${isOver ? "border-primary ring-1 ring-primary/40" : "border-border/60"}`}
      data-testid={`order-row-${q.question_id}`}
    >
      <GripVertical className={`w-4 h-4 shrink-0 ${draggable ? "cursor-grab text-muted-foreground" : "text-muted-foreground/30"}`} />
      <Input
        type="number" min={1} max={total} value={pos}
        onChange={(e) => setPos(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className="h-8 w-16 text-center px-1 tabular-nums"
        data-testid={`order-pos-${q.question_id}`}
        title="Type a position and press Enter"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate" data-testid={`order-title-${q.question_id}`}>{q.title}</p>
        <div className="flex items-center gap-2 flex-wrap mt-0.5">
          <StarRating value={q.difficulty} reasons={q.difficulty_reasons} source={q.difficulty_source} onChange={(n) => onRate(q.question_id, n)} testId={`order-stars-${q.question_id}`} />
          <Badge variant="outline" className="text-[10px]">{q.pool === "challenge" ? "Bonus" : q.pool === "warmup" ? "Warm-up" : "Daily"}</Badge>
          {q.date && q.date !== "None" && <Badge variant="outline" className="text-[10px] gap-1"><Calendar className="w-3 h-3" />{q.date}</Badge>}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Move to top" disabled={index === 0} onClick={() => onMove(q.question_id, 0)} data-testid={`order-top-${q.question_id}`}><ArrowUpToLine className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" title="Move to bottom" disabled={index === total - 1} onClick={() => onMove(q.question_id, total - 1)} data-testid={`order-bottom-${q.question_id}`}><ArrowDownToLine className="w-4 h-4" /></Button>
        {onEdit && <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit question" onClick={() => onEdit(q.question_id)} data-testid={`order-edit-${q.question_id}`}><Edit2 className="w-4 h-4" /></Button>}
      </div>
    </li>
  );
}

export function QuestionOrderTab({ onEditQuestion, isMasterAdmin = false }) {
  const [mode, setMode] = useState("python");
  const [list, setList] = useState("warmup");
  const [rows, setRows] = useState([]);
  const [saved, setSaved] = useState([]);
  const [scheduledHidden, setScheduledHidden] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmSort, setConfirmSort] = useState(null);
  const [autoRateOpen, setAutoRateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const dragId = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const applyRows = (data) => { setRows(data.questions); setSaved(data.questions); setScheduledHidden(data.scheduled_hidden || 0); };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/question-order?mode=${mode}&list=${list}`, { credentials: "include" });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail;
        throw new Error(typeof detail === "string" ? detail : `HTTP ${res.status}`);
      }
      applyRows(await res.json());
    } catch (e) { toast.error(`Could not load the campaign order: ${e.message}`); }
    setLoading(false);
  };
  useEffect(() => { load(); setSearch(""); }, [mode, list]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => rows.length !== saved.length || rows.some((q, i) => q.question_id !== saved[i]?.question_id), [rows, saved]);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return s ? rows.filter((q) => (q.title || "").toLowerCase().includes(s)) : rows;
  }, [rows, search]);
  const filtering = filtered.length !== rows.length;

  const moveTo = (qid, newIndex) => {
    setRows((prev) => {
      const from = prev.findIndex((q) => q.question_id === qid);
      if (from < 0) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(Math.min(newIndex, next.length), 0, item);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/question-order`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode, list, question_ids: rows.map((q) => q.question_id) }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(data.questions); setSaved(data.questions);
      toast.success(`Saved the ${MODE_META[mode].label} ${list === "warmup" ? "warm-up" : "bonus"} order`);
    } catch { toast.error("Could not save the order"); }
    setSaving(false);
  };

  const sortBy = async (by) => {
    setConfirmSort(null);
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/question-order/sort`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode, list, by }) });
      if (!res.ok) throw new Error();
      applyRows(await res.json());
      toast.success(by === "difficulty" ? "Sorted easiest to hardest" : `Sorted ${SORT_LABEL[by] || by}`);
    } catch { toast.error("Could not sort"); }
    setSaving(false);
  };

  const autoRate = async ({ overwrite, thenSort }) => {
    setAutoRateOpen(false);
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/question-order/auto-rate`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode, list, overwrite, then_sort: thenSort }) });
      if (!res.ok) throw new Error();
      const data = await res.json();
      applyRows(data);
      const bits = [`Rated ${data.rated} question${data.rated === 1 ? "" : "s"}`];
      if (data.kept_manual) bits.push(`${data.kept_manual} admin-set kept`);
      if (data.unratable?.length) bits.push(`${data.unratable.length} without a solution skipped`);
      if (data.sorted) bits.push("sorted easiest → hardest");
      toast.success(bits.join(" · "));
    } catch { toast.error("Could not auto-rate"); }
    setSaving(false);
  };

  const rate = async (qid, stars) => {
    const patch = (list_) => list_.map((q) => (q.question_id === qid ? { ...q, difficulty: stars, difficulty_source: "manual", difficulty_reasons: ["set by admin"] } : q));
    try {
      const res = await fetch(`${API}/admin/question-order/${qid}/difficulty`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ difficulty: stars }) });
      if (!res.ok) throw new Error();
      setRows(patch); setSaved(patch);
      toast.success(`Set to ${stars} star${stars === 1 ? "" : "s"}`);
    } catch { toast.error("Could not save the rating"); }
  };

  const unratedCount = rows.filter((q) => !q.difficulty).length;

  return (
    <div className="bg-card rounded-2xl border border-border/40 p-4 sm:p-6 space-y-5" data-testid="question-order-tab">
      <ConfirmSortDialog by={confirmSort} onCancel={() => setConfirmSort(null)} onConfirm={() => sortBy(confirmSort)} />
      <AutoRateDialog open={autoRateOpen} unrated={unratedCount} total={rows.length} onCancel={() => setAutoRateOpen(false)} onConfirm={autoRate} />
      {isMasterAdmin && <CampaignResetDialog open={resetOpen} onClose={() => setResetOpen(false)} />}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-primary/10"><Flag className="w-5 h-5 text-primary" /></div>
          <div>
            <h2 className="font-heading font-semibold text-lg">Campaign order</h2>
            <p className="text-sm text-muted-foreground">Registered users work through the campaign top to bottom, warm-ups and bonus challenges separately. Drag rows, type a position, or auto-sort in one click. Scheduled (dated) questions aren't listed here - they always come first on their day. New "×5 similar problems" slot in straight after their original.</p>
          </div>
        </div>
        {isMasterAdmin && (
          <Button variant="outline" size="sm" onClick={() => setResetOpen(true)} className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/5" data-testid="campaign-reset-all-btn">
            <RotateCcw className="w-3.5 h-3.5" /> Reset campaign for all users
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="order-mode-filter">
        {MODES.map((m) => { const meta = MODE_META[m]; const Icon = meta.icon; const active = mode === m; return (
          <button key={m} type="button" onClick={() => setMode(m)} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${active ? `${meta.pill} ${meta.border}` : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid={`order-mode-${m}`}>
            <Icon className="w-4 h-4" />{meta.label}
          </button>
        ); })}
        <span className="mx-1 h-6 w-px bg-border hidden sm:block" />
        {LISTS.map((l) => (
          <button key={l.id} type="button" onClick={() => setList(l.id)} className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${list === l.id ? "bg-foreground text-background border-foreground" : "bg-card text-muted-foreground border-border hover:text-foreground"}`} data-testid={`order-list-${l.id}`}>{l.label}</button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a question…" className="pl-8 h-9" data-testid="order-search" />
        </div>
        <Button variant="outline" size="sm" onClick={() => setAutoRateOpen(true)} disabled={saving || loading || !rows.length} className="gap-1.5 border-primary/40 text-primary" data-testid="order-autorate"><Sparkles className="w-3.5 h-3.5" /> Auto-rate difficulty{unratedCount ? ` (${unratedCount} unrated)` : ""}</Button>
        <Button variant="outline" size="sm" onClick={() => setConfirmSort("difficulty")} disabled={saving || loading || !rows.length} className="gap-1.5" data-testid="order-sort-difficulty"><Wand2 className="w-3.5 h-3.5" /> Auto-sort easiest → hardest</Button>
        <Button variant="outline" size="sm" onClick={() => setConfirmSort("title")} disabled={saving || loading || !rows.length} data-testid="order-sort-title">A → Z</Button>
        <span className="ml-auto text-xs text-muted-foreground" data-testid="order-total">
          {rows.length} question{rows.length === 1 ? "" : "s"}
          {scheduledHidden > 0 && <span data-testid="order-scheduled-hidden"> · {scheduledHidden} scheduled hidden</span>}
          {dirty ? " · unsaved changes" : ""}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setRows(saved)} disabled={!dirty || saving} className="gap-1.5" data-testid="order-reset"><RotateCcw className="w-3.5 h-3.5" /> Undo</Button>
        <Button size="sm" onClick={save} disabled={!dirty || saving} className="gap-1.5" data-testid="order-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save order</Button>
      </div>

      {filtering && <p className="text-xs text-muted-foreground" data-testid="order-filter-note">Showing {filtered.length} of {rows.length} - drag is off while filtering; use the position box or the top/bottom arrows.</p>}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-muted-foreground text-center py-12" data-testid="order-empty">No {MODE_META[mode].label} {list === "warmup" ? "warm-up" : "bonus"} questions yet.</p>
      ) : (
        <ol className="space-y-1.5" data-testid="order-list" onDragEnd={() => { dragId.current = null; setOverIndex(null); }}>
          {filtered.map((q) => {
            const index = rows.indexOf(q);
            return (
              <OrderRow
                key={q.question_id} q={q} index={index} total={rows.length}
                onMove={moveTo} onEdit={onEditQuestion} onRate={rate}
                draggable={!filtering}
                onDragStart={(id) => { dragId.current = id; }}
                onDragOver={(i) => { if (!filtering) setOverIndex(i); }}
                onDrop={(i) => { if (dragId.current && !filtering) moveTo(dragId.current, i); dragId.current = null; setOverIndex(null); }}
                isDragging={dragId.current === q.question_id}
                isOver={overIndex === index}
              />
            );
          })}
        </ol>
      )}
    </div>
  );
}
